import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Send, Mail, Bell, Users, MessageSquare } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface InstructorNotification {
  id: string;
  title: string;
  message: string;
  notification_type: string;
  target_audience: string[];
  sent_to_emails: boolean;
  sent_to_app: boolean;
  status: string;
  sent_at: string | null;
  created_at: string;
}

interface InstructorCommunicationProps {
  courseId: string;
}

export const InstructorCommunication: React.FC<InstructorCommunicationProps> = ({ courseId }) => {
  const [notifications, setNotifications] = useState<InstructorNotification[]>([]);
  const [newNotification, setNewNotification] = useState({
    title: '',
    message: '',
    notification_type: 'general',
    target_audience: ['all'],
    sent_to_emails: true,
    sent_to_app: true
  });
  const [loading, setLoading] = useState(false);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchNotifications();
  }, [courseId]);

  const fetchNotifications = async () => {
    try {
      const { data, error } = await supabase
        .from('instructor_notifications')
        .select('*')
        .eq('course_id', courseId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNotifications(data || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch notifications",
        variant: "destructive"
      });
    }
  };

  const createNotification = async () => {
    if (!newNotification.title.trim() || !newNotification.message.trim()) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const user = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('instructor_notifications')
        .insert({
          course_id: courseId,
          instructor_id: user.data.user?.id,
          ...newNotification
        })
        .select()
        .single();

      if (error) throw error;

      setNotifications([data, ...notifications]);
      setNewNotification({
        title: '',
        message: '',
        notification_type: 'general',
        target_audience: ['all'],
        sent_to_emails: true,
        sent_to_app: true
      });

      toast({
        title: "Success",
        description: "Notification created successfully"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create notification",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const sendNotification = async (notificationId: string) => {
    setSendingId(notificationId);
    try {
      // In a real implementation, this would trigger an edge function
      // to send emails and create app notifications
      const { error } = await supabase
        .from('instructor_notifications')
        .update({ 
          status: 'sent',
          sent_at: new Date().toISOString()
        })
        .eq('id', notificationId);

      if (error) throw error;

      await fetchNotifications();
      toast({
        title: "Success",
        description: "Notification sent successfully"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send notification",
        variant: "destructive"
      });
    } finally {
      setSendingId(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'bg-green-100 text-green-800';
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'attendance': return <Users className="h-4 w-4" />;
      case 'survey': return <MessageSquare className="h-4 w-4" />;
      case 'announcement': return <Bell className="h-4 w-4" />;
      default: return <Mail className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Create New Notification
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium">Title *</label>
            <Input
              value={newNotification.title}
              onChange={(e) => setNewNotification({...newNotification, title: e.target.value})}
              placeholder="Notification title..."
            />
          </div>

          <div>
            <label className="text-sm font-medium">Message *</label>
            <Textarea
              value={newNotification.message}
              onChange={(e) => setNewNotification({...newNotification, message: e.target.value})}
              placeholder="Your message to participants..."
              className="min-h-[100px]"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Type</label>
              <Select 
                value={newNotification.notification_type} 
                onValueChange={(value) => setNewNotification({...newNotification, notification_type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="attendance">Attendance</SelectItem>
                  <SelectItem value="survey">Survey</SelectItem>
                  <SelectItem value="announcement">Announcement</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Target Audience</label>
              <Select 
                value={newNotification.target_audience[0]} 
                onValueChange={(value) => setNewNotification({...newNotification, target_audience: [value]})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Participants</SelectItem>
                  <SelectItem value="enrolled">Enrolled Only</SelectItem>
                  <SelectItem value="completed">Completed Only</SelectItem>
                  <SelectItem value="absent">Recent Absences</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="email"
                checked={newNotification.sent_to_emails}
                onCheckedChange={(checked) => setNewNotification({...newNotification, sent_to_emails: !!checked})}
              />
              <label htmlFor="email" className="text-sm">Send via Email</label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="app"
                checked={newNotification.sent_to_app}
                onCheckedChange={(checked) => setNewNotification({...newNotification, sent_to_app: !!checked})}
              />
              <label htmlFor="app" className="text-sm">Send to App</label>
            </div>
          </div>

          <Button onClick={createNotification} disabled={loading} className="w-full">
            {loading ? 'Creating...' : 'Create Notification'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Notification History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {notifications.map((notification) => (
              <div key={notification.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {getTypeIcon(notification.notification_type)}
                      <h4 className="font-medium">{notification.title}</h4>
                      <Badge className={getStatusColor(notification.status)}>
                        {notification.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      {notification.message}
                    </p>
                    <div className="flex gap-4 text-xs text-muted-foreground">
                      <span>Type: {notification.notification_type}</span>
                      <span>Audience: {notification.target_audience.join(', ')}</span>
                      <span>Created: {new Date(notification.created_at).toLocaleDateString()}</span>
                      {notification.sent_at && (
                        <span>Sent: {new Date(notification.sent_at).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                  {notification.status === 'draft' && (
                    <Button 
                      size="sm"
                      onClick={() => sendNotification(notification.id)}
                      disabled={sendingId === notification.id}
                    >
                      {sendingId === notification.id ? 'Sending...' : 'Send Now'}
                    </Button>
                  )}
                </div>
              </div>
            ))}

            {notifications.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No notifications created yet.
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};